// services/reportService.js — GM Excel Report + User Email Reports
import XLSX from "xlsx-js-style";
import { mailSendRequest } from "../authConfig";
import { latestProgressNote } from "./activityService";

// ── Style palette ──────────────────────────────────────────────────────────────
const NAVY   = "001A3D";
const DBLUE  = "002D7A";
const BLUE   = "0066FF";
const WHITE  = "FFFFFF";
const LBLUE  = "DCE6FF";
const ALT    = "F4F7FF";
const DARK   = "1A1A2E";
const GREY   = "B4BFCC";

const THIN_BORDER = {
  top:    { style: "thin", color: { rgb: GREY } },
  bottom: { style: "thin", color: { rgb: GREY } },
  left:   { style: "thin", color: { rgb: GREY } },
  right:  { style: "thin", color: { rgb: GREY } },
};
const MED_BORDER = {
  top:    { style: "medium", color: { rgb: NAVY } },
  bottom: { style: "medium", color: { rgb: NAVY } },
  left:   { style: "medium", color: { rgb: NAVY } },
  right:  { style: "medium", color: { rgb: NAVY } },
};

const S = {
  title: {
    font:      { name: "Helvetica", sz: 14, bold: true, color: { rgb: WHITE } },
    fill:      { patternType: "solid", fgColor: { rgb: NAVY } },
    alignment: { horizontal: "center", vertical: "center", wrapText: true },
    border:    MED_BORDER,
  },
  subtitle: {
    font:      { name: "Helvetica", sz: 14, bold: true, color: { rgb: WHITE } },
    fill:      { patternType: "solid", fgColor: { rgb: BLUE } },
    alignment: { horizontal: "center", vertical: "center" },
    border:    THIN_BORDER,
  },
  header: {
    font:      { name: "Helvetica", sz: 14, bold: true, color: { rgb: WHITE } },
    fill:      { patternType: "solid", fgColor: { rgb: DBLUE } },
    alignment: { horizontal: "center", vertical: "center", wrapText: true },
    border:    THIN_BORDER,
  },
  staffName: {
    font:      { name: "Helvetica", sz: 14, bold: true, color: { rgb: NAVY } },
    fill:      { patternType: "solid", fgColor: { rgb: LBLUE } },
    alignment: { horizontal: "center", vertical: "center" },
    border:    THIN_BORDER,
  },
  dataC: (even) => ({
    font:      { name: "Helvetica", sz: 14, color: { rgb: DARK } },
    fill:      { patternType: "solid", fgColor: { rgb: even ? WHITE : ALT } },
    alignment: { horizontal: "center", vertical: "top", wrapText: true },
    border:    THIN_BORDER,
  }),
  dataL: (even) => ({
    font:      { name: "Helvetica", sz: 14, color: { rgb: DARK } },
    fill:      { patternType: "solid", fgColor: { rgb: even ? WHITE : ALT } },
    alignment: { horizontal: "left", vertical: "top", wrapText: true },
    border:    THIN_BORDER,
  }),
};

function mkCell(value, style, type) {
  const v = value === null || value === undefined ? "" : value;
  const t = type || (typeof v === "number" ? "n" : "s");
  return { v, t, s: style };
}

const COL_HEADERS = [
  "S/No", "RESPONSIBLE PARTY", "USER DEPT", "ENTITY/ASSET",
  "DESCRIPTION", "CONTRACTOR/VENDOR", "KEY ACTIVITIES", "LOOK AHEAD", "ACTION PARTY",
];

const COL_WIDTHS = [
  { wch: 5 }, { wch: 22 }, { wch: 22 }, { wch: 14 }, { wch: 45 },
  { wch: 25 }, { wch: 55 }, { wch: 45 }, { wch: 22 },
];

// ── Shared workbook builder ────────────────────────────────────────────────────
function buildGMWorkbook(activities, dateLabel) {
  const wb = XLSX.utils.book_new();

  const byStaff = {};
  activities.forEach((a) => {
    const name = a.assignedToName || a.assignedTo || "Unassigned";
    if (!byStaff[name]) byStaff[name] = [];
    byStaff[name].push(a);
  });

  if (Object.keys(byStaff).length === 0) {
    const ws = XLSX.utils.aoa_to_sheet([[mkCell("No activities found for this period.", S.dataL(true))]]);
    XLSX.utils.book_append_sheet(wb, ws, "Summary");
    return wb;
  }

  Object.entries(byStaff).forEach(([staffName, staffActivities]) => {
    const aoa = [
      // Row 1: Main title (merged A1:I1)
      [mkCell(`GENERAL MANAGER REPORT- (Week ending- ${dateLabel})`, S.title)],
      // Row 2: Spacer
      [],
      // Row 3: "ACTIVITY REPORT" label (merged)
      [mkCell("ACTIVITY REPORT", S.subtitle)],
      // Row 4: Column headers
      COL_HEADERS.map((h) => mkCell(h, S.header)),
      // Row 5: Staff name (merged)
      [mkCell(staffName.toUpperCase(), S.staffName)],
    ];

    staffActivities.forEach((a, i) => {
      const even = i % 2 === 0;
      const keyActivities = latestProgressNote(a);
      const lookAhead     = a.lookAhead || a.updates?.[0]?.lookAhead || "";
      aoa.push([
        mkCell(i + 1,                    S.dataC(even), "n"),
        mkCell(a.assignedToName || "",   S.dataL(even)),
        mkCell(a.userDept || "",         S.dataL(even)),
        mkCell(a.entity || "",           S.dataC(even)),
        mkCell(a.title || "",            S.dataL(even)),
        mkCell(a.contractor || "",       S.dataL(even)),
        mkCell(keyActivities,            S.dataL(even)),
        mkCell(lookAhead,                S.dataL(even)),
        mkCell(a.assignedToName || "",   S.dataL(even)),
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 8 } },  // Title row
      { s: { r: 2, c: 0 }, e: { r: 2, c: 8 } },  // Activity Report row
      { s: { r: 4, c: 0 }, e: { r: 4, c: 8 } },  // Staff name row
    ];

    ws["!cols"] = COL_WIDTHS;

    ws["!rows"] = [
      { hpx: 52 },  // Title
      { hpx: 8  },  // Spacer
      { hpx: 36 },  // Activity Report
      { hpx: 44 },  // Headers
      { hpx: 30 },  // Staff name
    ];

    XLSX.utils.book_append_sheet(wb, ws, staffName.substring(0, 31));
  });

  return wb;
}

// ── GM Excel download ──────────────────────────────────────────────────────────
export function generateGMExcel(activities, weekEndingDate) {
  const dateLabel = weekEndingDate
    ? new Date(weekEndingDate).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const wb = buildGMWorkbook(activities, dateLabel);
  const filename = `GM_Activity_Report_${weekEndingDate || new Date().toISOString().slice(0, 10)}.xlsx`;
  XLSX.writeFile(wb, filename, { cellStyles: true });
  return filename;
}

// ── GM Email Report ────────────────────────────────────────────────────────────
export async function sendGMReport(instance, accounts, activities, gmEmail, gmName, senderName, weekEndingDate) {
  const dateLabel = weekEndingDate
    ? new Date(weekEndingDate).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const wb = buildGMWorkbook(activities, dateLabel);
  const filename = `GM_Activity_Report_${weekEndingDate || new Date().toISOString().slice(0, 10)}.xlsx`;
  const b64 = XLSX.write(wb, { bookType: "xlsx", type: "base64", cellStyles: true });

  let token = null;
  try {
    const resp = await instance.acquireTokenSilent({ ...mailSendRequest, account: accounts[0] });
    token = resp.accessToken;
  } catch {
    try {
      const resp = await instance.acquireTokenPopup({ ...mailSendRequest, account: accounts[0] });
      token = resp.accessToken;
    } catch (err) {
      throw new Error("Could not acquire Mail.Send permission: " + err.message);
    }
  }

  const shortDate = weekEndingDate
    ? new Date(weekEndingDate).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  const bodyHtml = `<p>Dear ${escHtml(gmName || "General Manager")},</p>
<p>Please find attached the SCM Activity Report for the week ending <strong>${escHtml(shortDate)}</strong>.</p>
<p>This report contains one sheet per SCM staff member covering key activities completed and the look-ahead for the coming week.</p>
<br><p>Best regards,<br><strong>${escHtml(senderName)}</strong><br>SCM Department</p>`;

  const res = await fetch("https://graph.microsoft.com/v1.0/me/sendMail", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({
      message: {
        subject: `SCM Activity Report — Week Ending ${shortDate}`,
        body: { contentType: "HTML", content: bodyHtml },
        toRecipients: [{ emailAddress: { address: gmEmail, name: gmName || "General Manager" } }],
        attachments: [{
          "@odata.type": "#microsoft.graph.fileAttachment",
          name: filename,
          contentType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          contentBytes: b64,
        }],
      },
      saveToSentItems: true,
    }),
  });

  if (!res.ok && res.status !== 202) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error?.message || `Failed to send GM report (HTTP ${res.status})`);
  }

  return { gmEmail, gmName, filename };
}

// ── User Email Reports ─────────────────────────────────────────────────────────
export async function sendUserReports(instance, accounts, activities, senderName, weekEndingDate, deptOwnerMap = {}) {
  const byOwner = {};

  function addToOwner(email, name, activity) {
    if (!email) return;
    if (!byOwner[email]) byOwner[email] = { name: name || email, items: [] };
    if (!byOwner[email].items.find((x) => x.id === activity.id)) {
      byOwner[email].items.push(activity);
    }
  }

  activities.forEach((a) => {
    const deptOwners = deptOwnerMap[a.userDept];
    if (deptOwners && deptOwners.length > 0) {
      deptOwners.forEach((o) => addToOwner(o.email, o.name, a));
    } else {
      addToOwner(a.ownerEmail, a.ownerName, a);
    }
  });

  if (Object.keys(byOwner).length === 0) return [];

  let token = null;
  try {
    const resp = await instance.acquireTokenSilent({ ...mailSendRequest, account: accounts[0] });
    token = resp.accessToken;
  } catch {
    try {
      const resp = await instance.acquireTokenPopup({ ...mailSendRequest, account: accounts[0] });
      token = resp.accessToken;
    } catch (err) {
      throw new Error("Could not acquire Mail.Send permission: " + err.message);
    }
  }

  const dateLabel = weekEndingDate
    ? new Date(weekEndingDate).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  const results = [];

  for (const [ownerEmail, { name: ownerName, items }] of Object.entries(byOwner)) {
    const subject = `SCM Activity Update — Week Ending ${dateLabel}`;
    const html = buildUserReportHtml(items, ownerName, senderName, dateLabel);

    try {
      const res = await fetch("https://graph.microsoft.com/v1.0/me/sendMail", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          message: {
            subject,
            body: { contentType: "HTML", content: html },
            toRecipients: [{ emailAddress: { address: ownerEmail, name: ownerName } }],
          },
          saveToSentItems: true,
        }),
      });
      results.push({ ownerEmail, ownerName, method: res.ok || res.status === 202 ? "graph" : "failed" });
    } catch {
      results.push({ ownerEmail, ownerName, method: "failed" });
    }
  }

  return results;
}

// ── Email HTML builder ─────────────────────────────────────────────────────────
function buildUserReportHtml(activities, ownerName, senderName, dateLabel) {
  const STATUS_COLORS = {
    "In Progress": { bg: "#EBF3FF", color: "#005A9E" },
    "Completed":   { bg: "#E9F5E9", color: "#107C10" },
    "On Hold":     { bg: "#FFF4EC", color: "#E65C00" },
    "Not Started": { bg: "#F0F0F0", color: "#605E5C" },
  };

  const rows = activities.map((a, i) => {
    const keyActivities = latestProgressNote(a) || "No update recorded.";
    const lookAhead = a.lookAhead || a.updates?.[0]?.lookAhead || "—";
    const ss = STATUS_COLORS[a.status] || STATUS_COLORS["Not Started"];
    return `
      <tr style="background:${i % 2 === 0 ? "#fff" : "#F8F9FC"};">
        <td style="padding:14px 12px;vertical-align:top;border-bottom:1px solid #E8EAED;font-weight:700;color:#001A3D;font-size:13px;min-width:180px;">
          ${escHtml(a.title)}
          ${a.userDept ? `<div style="font-size:11px;color:#888;margin-top:3px;">${escHtml(a.userDept)}${a.entity && a.entity !== "N/A" ? ` · ${escHtml(a.entity)}` : ""}</div>` : ""}
          ${a.contractor ? `<div style="font-size:11px;color:#555;margin-top:2px;">Vendor: ${escHtml(a.contractor)}</div>` : ""}
        </td>
        <td style="padding:14px 12px;vertical-align:top;border-bottom:1px solid #E8EAED;font-size:13px;color:#333;line-height:1.6;min-width:200px;">
          ${escHtml(keyActivities)}
        </td>
        <td style="padding:14px 12px;vertical-align:top;border-bottom:1px solid #E8EAED;font-size:13px;color:#333;line-height:1.6;min-width:180px;">
          ${escHtml(lookAhead)}
        </td>
        <td style="padding:14px 12px;vertical-align:top;border-bottom:1px solid #E8EAED;text-align:center;white-space:nowrap;">
          <span style="background:${ss.bg};color:${ss.color};padding:3px 10px;border-radius:20px;font-size:11px;font-weight:700;">${escHtml(a.status)}</span>
        </td>
      </tr>`;
  }).join("");

  return `<!DOCTYPE html>
<html>
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:#F0F4F8;font-family:Segoe UI,Arial,sans-serif;">
  <div style="max-width:900px;margin:32px auto;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 4px 20px rgba(0,0,0,0.10);">
    <div style="background:linear-gradient(135deg,#0066FF,#002D7A);padding:28px 36px;">
      <div style="color:#fff;font-size:22px;font-weight:800;letter-spacing:-0.5px;">SCM Activity Update</div>
      <div style="color:#C8D8FF;font-size:14px;margin-top:4px;">Week Ending ${escHtml(dateLabel)}</div>
    </div>
    <div style="padding:24px 36px 16px;border-bottom:1px solid #EDEBE9;">
      <p style="margin:0;font-size:14px;color:#201F1E;line-height:1.7;">
        Dear <strong>${escHtml(ownerName)}</strong>,<br>
        Please find below the SCM activity update for your contracts and tasks for the week ending <strong>${escHtml(dateLabel)}</strong>.
        This report covers the latest progress, key activities completed, and the look-ahead for the coming week.
      </p>
    </div>
    <div style="padding:24px 36px;overflow-x:auto;">
      <table style="width:100%;border-collapse:collapse;font-size:13px;">
        <thead>
          <tr style="background:#001A3D;">
            <th style="padding:12px 12px;text-align:left;color:#C8D8FF;font-size:11px;font-weight:700;letter-spacing:0.8px;white-space:nowrap;">DESCRIPTION</th>
            <th style="padding:12px 12px;text-align:left;color:#C8D8FF;font-size:11px;font-weight:700;letter-spacing:0.8px;white-space:nowrap;">KEY ACTIVITIES</th>
            <th style="padding:12px 12px;text-align:left;color:#C8D8FF;font-size:11px;font-weight:700;letter-spacing:0.8px;white-space:nowrap;">LOOK AHEAD</th>
            <th style="padding:12px 12px;text-align:center;color:#C8D8FF;font-size:11px;font-weight:700;letter-spacing:0.8px;white-space:nowrap;">STATUS</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
    <div style="background:#F8F9FF;padding:20px 36px;border-top:1px solid #E3EEFF;">
      <p style="margin:0;font-size:12px;color:#888;line-height:1.7;">
        This report was prepared by <strong>${escHtml(senderName)}</strong> on behalf of the LekOil SCM Department.<br>
        For queries or clarifications, please contact the SCM team directly.
      </p>
    </div>
  </div>
</body>
</html>`;
}

// ── CFO Bi-weekly Issue List ───────────────────────────────────────────────────

const PURPLE  = "7B2D8B";
const LPURPLE = "F0E0F8";
const ALTPUR  = "FAF0FF";

const CFO_HEADERS = [
  "S/N", "ACTIVITY / CONTRACT DESCRIPTION", "DEPT / ENTITY", "STATUS",
  "PRIORITY", "DUE DATE", "ASSIGNED TO (SCM)", "OWNER(S)",
  "KEY ACTIVITIES", "LOOK AHEAD", "RECOMMENDATIONS",
];
const CFO_WIDTHS = [
  { wch: 5 }, { wch: 46 }, { wch: 22 }, { wch: 14 },
  { wch: 11 }, { wch: 14 }, { wch: 22 }, { wch: 22 },
  { wch: 46 }, { wch: 36 }, { wch: 46 },
];

const SCFO = {
  title: {
    font:      { name: "Helvetica", sz: 14, bold: true, color: { rgb: WHITE } },
    fill:      { patternType: "solid", fgColor: { rgb: PURPLE } },
    alignment: { horizontal: "center", vertical: "center", wrapText: true },
    border:    MED_BORDER,
  },
  subtitle: {
    font:      { name: "Helvetica", sz: 14, bold: true, color: { rgb: WHITE } },
    fill:      { patternType: "solid", fgColor: { rgb: NAVY } },
    alignment: { horizontal: "center", vertical: "center" },
    border:    THIN_BORDER,
  },
  header: {
    font:      { name: "Helvetica", sz: 14, bold: true, color: { rgb: WHITE } },
    fill:      { patternType: "solid", fgColor: { rgb: DBLUE } },
    alignment: { horizontal: "center", vertical: "center", wrapText: true },
    border:    THIN_BORDER,
  },
  dataC: (even) => ({
    font:      { name: "Helvetica", sz: 14, color: { rgb: DARK } },
    fill:      { patternType: "solid", fgColor: { rgb: even ? WHITE : ALTPUR } },
    alignment: { horizontal: "center", vertical: "top", wrapText: true },
    border:    THIN_BORDER,
  }),
  dataL: (even) => ({
    font:      { name: "Helvetica", sz: 14, color: { rgb: DARK } },
    fill:      { patternType: "solid", fgColor: { rgb: even ? WHITE : ALTPUR } },
    alignment: { horizontal: "left", vertical: "top", wrapText: true },
    border:    THIN_BORDER,
  }),
  recCell: (even) => ({
    font:      { name: "Helvetica", sz: 13, italic: true, color: { rgb: PURPLE } },
    fill:      { patternType: "solid", fgColor: { rgb: even ? LPURPLE : "EDD8F8" } },
    alignment: { horizontal: "left", vertical: "top", wrapText: true },
    border:    THIN_BORDER,
  }),
};

function buildCFOWorkbook(activities, periodLabel) {
  const wb  = XLSX.utils.book_new();
  const nCols = CFO_HEADERS.length - 1; // 0-based last col index = 10

  if (activities.length === 0) {
    const ws = XLSX.utils.aoa_to_sheet([[mkCell("No CFO Issues found for this period.", SCFO.dataL(true))]]);
    XLSX.utils.book_append_sheet(wb, ws, "CFO Issues");
    return wb;
  }

  const aoa = [
    [mkCell(`CFO BI-WEEKLY ISSUE LIST — ${periodLabel}`, SCFO.title)],
    [],
    [mkCell("LEKOIL LIMITED — SUPPLY CHAIN MANAGEMENT DEPARTMENT", SCFO.subtitle)],
    CFO_HEADERS.map((h) => mkCell(h, SCFO.header)),
  ];

  activities.forEach((a, i) => {
    const even = i % 2 === 0;
    const keyAct = latestProgressNote(a);
    const lookAhead = a.lookAhead || a.updates?.[0]?.lookAhead || "";
    const deptEntity = [a.userDept, a.entity && a.entity !== "N/A" ? a.entity : ""].filter(Boolean).join(" / ");
    const owners = a.owners?.length > 0
      ? a.owners.map(o => o.name || o.email).join(", ")
      : (a.ownerName || "");
    const recs = (a.cfoRecommendations || [])
      .map((r, j) => `${j + 1}. [${new Date(r.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} – ${r.byName || r.by}]: ${r.text}`)
      .join("\n\n");
    const dueDate = a.dueDate
      ? new Date(a.dueDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
      : "";

    aoa.push([
      mkCell(i + 1,              SCFO.dataC(even), "n"),
      mkCell(a.title || "",      SCFO.dataL(even)),
      mkCell(deptEntity,         SCFO.dataL(even)),
      mkCell(a.status || "",     SCFO.dataC(even)),
      mkCell(a.priority || "",   SCFO.dataC(even)),
      mkCell(dueDate,            SCFO.dataC(even)),
      mkCell(a.assignedToName || a.assignedTo || "", SCFO.dataL(even)),
      mkCell(owners,             SCFO.dataL(even)),
      mkCell(keyAct,             SCFO.dataL(even)),
      mkCell(lookAhead,          SCFO.dataL(even)),
      mkCell(recs || "—",        SCFO.recCell(even)),
    ]);
  });

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  ws["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: nCols } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: nCols } },
  ];

  ws["!cols"]  = CFO_WIDTHS;
  ws["!rows"]  = [{ hpx: 52 }, { hpx: 8 }, { hpx: 36 }, { hpx: 44 }];

  XLSX.utils.book_append_sheet(wb, ws, "CFO Issues");
  return wb;
}

export function generateCFOExcel(activities, periodDate) {
  const label = periodDate
    ? new Date(periodDate).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const wb  = buildCFOWorkbook(activities, label);
  const filename = `CFO_Issue_List_${periodDate || new Date().toISOString().slice(0, 10)}.xlsx`;
  XLSX.writeFile(wb, filename, { cellStyles: true });
  return filename;
}

// ── Benchmark Pricing Database Excel ──────────────────────────────────────────

const AMBER  = "A66400";
const LAMBR  = "FFF3DC";
const ALAMBR = "FFFBF0";
const DGOLD  = "C87800";

const BM_HEADERS = [
  "S/N", "TENDER REFERENCE", "CATEGORY", "PHASE", "LOCATION",
  "COMMODITY GROUP", "SUPPLIER NAME", "UNIT OF MEASURE", "CURRENCY",
  "BENCHMARK RATE", "LOWEST RATE", "AWARDED RATE", "% CHANGE",
  "TENDER DATE", "CONTRACT DURATION", "COST SAVING",
  "SERVICE DESCRIPTION", "COMMERCIAL TERMS", "AWARD JUSTIFICATION", "MARKET OBSERVATION",
  "SUBMITTED BY",
];

const BM_WIDTHS = [
  { wch: 5 }, { wch: 30 }, { wch: 20 }, { wch: 18 }, { wch: 16 },
  { wch: 20 }, { wch: 24 }, { wch: 14 }, { wch: 8 },
  { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 10 },
  { wch: 14 }, { wch: 16 }, { wch: 16 },
  { wch: 40 }, { wch: 30 }, { wch: 30 }, { wch: 30 },
  { wch: 22 },
];

const SBM = {
  title: {
    font:      { name: "Helvetica", sz: 14, bold: true, color: { rgb: WHITE } },
    fill:      { patternType: "solid", fgColor: { rgb: AMBER } },
    alignment: { horizontal: "center", vertical: "center", wrapText: true },
    border:    MED_BORDER,
  },
  subtitle: {
    font:      { name: "Helvetica", sz: 14, bold: true, color: { rgb: WHITE } },
    fill:      { patternType: "solid", fgColor: { rgb: NAVY } },
    alignment: { horizontal: "center", vertical: "center" },
    border:    THIN_BORDER,
  },
  header: {
    font:      { name: "Helvetica", sz: 14, bold: true, color: { rgb: WHITE } },
    fill:      { patternType: "solid", fgColor: { rgb: DGOLD } },
    alignment: { horizontal: "center", vertical: "center", wrapText: true },
    border:    THIN_BORDER,
  },
  dataC: (even) => ({
    font:      { name: "Helvetica", sz: 13, color: { rgb: DARK } },
    fill:      { patternType: "solid", fgColor: { rgb: even ? WHITE : ALAMBR } },
    alignment: { horizontal: "center", vertical: "top", wrapText: true },
    border:    THIN_BORDER,
  }),
  dataL: (even) => ({
    font:      { name: "Helvetica", sz: 13, color: { rgb: DARK } },
    fill:      { patternType: "solid", fgColor: { rgb: even ? WHITE : ALAMBR } },
    alignment: { horizontal: "left", vertical: "top", wrapText: true },
    border:    THIN_BORDER,
  }),
  dataNum: (even, positive) => ({
    font:      { name: "Helvetica", sz: 13, bold: true, color: { rgb: positive === null ? DARK : positive ? "C4262E" : "107C10" } },
    fill:      { patternType: "solid", fgColor: { rgb: even ? WHITE : ALAMBR } },
    alignment: { horizontal: "right", vertical: "top", wrapText: true },
    border:    THIN_BORDER,
    numFmt:    "#,##0.00",
  }),
  pctCell: (even, value) => {
    const isPos = value > 0;
    const isNeg = value < 0;
    return {
      font:      { name: "Helvetica", sz: 13, bold: true, color: { rgb: isNeg ? "107C10" : isPos ? "C4262E" : DARK } },
      fill:      { patternType: "solid", fgColor: { rgb: isNeg ? "EAF6EA" : isPos ? "FDE7E9" : (even ? WHITE : ALAMBR) } },
      alignment: { horizontal: "center", vertical: "top", wrapText: true },
      border:    THIN_BORDER,
    };
  },
};

const PHASE_LABELS_BRIEF = { 1: "Phase 1 – High Value", 2: "Phase 2 – Mid Value", 3: "Phase 3 – Low Value" };

function buildBenchmarkWorkbook(records, dateLabel) {
  const wb     = XLSX.utils.book_new();
  const nCols  = BM_HEADERS.length - 1;

  if (records.length === 0) {
    const ws = XLSX.utils.aoa_to_sheet([[mkCell("No benchmark records found.", SBM.dataL(true))]]);
    XLSX.utils.book_append_sheet(wb, ws, "Benchmark Pricing");
    return wb;
  }

  // Group by category for category header rows
  const grouped = {};
  records.forEach(r => {
    const cat = r.category || "Other";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(r);
  });

  const aoa = [
    [mkCell(`SCM BENCHMARK PRICING DATABASE — ${dateLabel}`, SBM.title)],
    [],
    [mkCell("LEKOIL LIMITED — SUPPLY CHAIN MANAGEMENT DEPARTMENT", SBM.subtitle)],
    BM_HEADERS.map(h => mkCell(h, SBM.header)),
  ];

  const merges = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: nCols } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: nCols } },
  ];

  let rowIdx   = 4; // 0-indexed, after header rows
  let serialNo = 1;

  Object.entries(grouped).forEach(([category, recs]) => {
    // Category group header
    const catHeaderStyle = {
      font:      { name: "Helvetica", sz: 13, bold: true, color: { rgb: AMBER } },
      fill:      { patternType: "solid", fgColor: { rgb: LAMBR } },
      alignment: { horizontal: "left", vertical: "center", wrapText: false },
      border:    THIN_BORDER,
    };
    aoa.push([mkCell(`  ${category.toUpperCase()} (${recs.length} record${recs.length !== 1 ? "s" : ""})`, catHeaderStyle)]);
    merges.push({ s: { r: rowIdx, c: 0 }, e: { r: rowIdx, c: nCols } });
    rowIdx++;

    recs.forEach((r, i) => {
      const even = i % 2 === 0;
      const pct  = r.percentageChange || 0;
      const dateStr = r.tenderDate
        ? new Date(r.tenderDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
        : "";

      aoa.push([
        mkCell(serialNo++,              SBM.dataC(even), "n"),
        mkCell(r.tenderRef || "",       SBM.dataL(even)),
        mkCell(r.category || "",        SBM.dataL(even)),
        mkCell(PHASE_LABELS_BRIEF[r.phase] || "Phase 3", SBM.dataL(even)),
        mkCell(r.location || "",        SBM.dataL(even)),
        mkCell(r.commodityGroup || "",  SBM.dataL(even)),
        mkCell(r.supplierName || "",    SBM.dataL(even)),
        mkCell(r.unitOfMeasure || "",   SBM.dataC(even)),
        mkCell(r.currency || "NGN",     SBM.dataC(even)),
        mkCell(r.benchmarkRate  || 0,   SBM.dataNum(even, null), "n"),
        mkCell(r.lowestRate     || 0,   SBM.dataNum(even, null), "n"),
        mkCell(r.awardedRate    || 0,   SBM.dataNum(even, null), "n"),
        mkCell(r.benchmarkRate > 0 ? `${pct > 0 ? "+" : ""}${pct.toFixed(1)}%` : "—", SBM.pctCell(even, pct)),
        mkCell(dateStr,                 SBM.dataC(even)),
        mkCell(r.contractDuration || "",SBM.dataC(even)),
        mkCell(r.costSavingAchieved || 0, SBM.dataNum(even, null), "n"),
        mkCell(r.serviceDescription  || "", SBM.dataL(even)),
        mkCell(r.commercialTerms     || "", SBM.dataL(even)),
        mkCell(r.awardJustification  || "", SBM.dataL(even)),
        mkCell(r.marketObservation   || "", SBM.dataL(even)),
        mkCell(r.submittedByName || r.submittedBy || "", SBM.dataL(even)),
      ]);
      rowIdx++;
    });
  });

  const ws      = XLSX.utils.aoa_to_sheet(aoa);
  ws["!merges"] = merges;
  ws["!cols"]   = BM_WIDTHS;
  ws["!rows"]   = [{ hpx: 52 }, { hpx: 8 }, { hpx: 36 }, { hpx: 44 }];

  XLSX.utils.book_append_sheet(wb, ws, "Benchmark Pricing");
  return wb;
}

export function generateBenchmarkExcel(records, reportDate) {
  const label = reportDate
    ? new Date(reportDate).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  const wb       = buildBenchmarkWorkbook(records, label);
  const filename = `SCM_Benchmark_Pricing_${reportDate || new Date().toISOString().slice(0, 10)}.xlsx`;
  XLSX.writeFile(wb, filename, { cellStyles: true });
  return filename;
}

function escHtml(str) {
  if (!str) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
